import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AssignClientChart from "../custom/AssignClientChartButton";
import {
    Edit,
    BooleanInput,
    Create,
    TextField,
    EditButton,
    SimpleForm,
    TextInput,
    FunctionField,
    Pagination,
    Show,
    SimpleShowLayout,
    ShowButton,
    BooleanField,
    useNotify,
} from "react-admin";

import BulkEditButton from "../core/BulkEditButton";
import SiteBulkEdit from "../custom/editors/SiteBulkEdit";
import { LVEditToolbar, LVCreateToolbar } from "../core/toolbars";
import BulkActionList from "../custom/BulkActionList";

import Filter from "../core/Filter";
import buildNewUrl from "../../helpers/buildNewUrl";

/*
    To configure pagination - we should have a look at this
    https://github.com/marmelab/react-admin/blob/master/docs/List.md#pagination
*/

let currentSiteId = null;

const validateSiteCreation = (values) => {
    const errors = {};

    if (values.site_group_id) {
        currentSiteId = values.site_group_id;
    }

    if (!values.client_id) {
        errors.client_id = "The client id field is required";
    }

    return errors;
};

const getEntityIds = () => {
    if (!currentSiteId) {
        alert("siteId not assigned");
        return null;
    }

    return { siteIds: [currentSiteId] };
};

const SiteBulkActionList = ({ onBulkEdit, filter, ...props }) => {
    const ListPagination = (props) => <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />;
    return (
        <BulkActionList
            resource="portal/sites"
            perPage={100}
            pagination={<ListPagination />}
            fields={[
                { type: TextField, props: { source: "id" } },
                { type: TextField, props: { source: "encodedId" } },
                { type: TextField, props: { source: "site_group_name" } },
                { type: TextField, props: { label: "Line ID", source: "lineId" } },
                { type: TextField, props: { source: "lineName" } },
                { type: TextField, props: { source: "modelType" } },
                {
                    type: FunctionField,
                    props: {
                        source: "available_to_user",
                        render: (record) => (record.available_to_user ? "True" : "False"),
                    },
                },
                { type: TextField, props: { source: "status" } },
                {
                    type: FunctionField,
                    props: {
                        label: "Uptime Monitored",
                        source: "uptime_monitored",
                        render: (record) => (record.uptime_monitored ? "True" : "False"),
                    },
                },
                {
                    type: FunctionField,
                    props: {
                        label: "Customer Visibility",
                        source: "customer_visibility_dttm",
                        render: (record) =>
                            record.customer_visibility_dttm
                                ? new Date(record.customer_visibility_dttm).toLocaleString("sv-SE", { timeZone: "UTC" }) + " UTC"
                                : "",
                    },
                },
                { type: ShowButton, props: {} },
                { type: EditButton, props: {} },
            ]}
            filter={filter}
            bulkActionButtons={<BulkEditButton onClick={(selectedIds) => onBulkEdit(selectedIds)} />}
            {...props}
        />
    );
};

export const SitesList = (props) => {
    const notify = useNotify();

    const [bulkEditing, setBulkEditing] = useState(false);
    const [selectedSiteIds, setSelectedSiteIds] = useState([]);

    const location = useLocation();
    const navigate = useNavigate();

    const urlSearchParams = new URLSearchParams(location.search);
    const lineIdsFilter = urlSearchParams.get("lineIdsFilter") || "";
    const lineNameFilter = urlSearchParams.get("lineNameFilter") || "";
    const siteGroupNameFilter = urlSearchParams.get("siteGroupNameFilter") || "";

    return (
        (bulkEditing && (
            <SiteBulkEdit
                selectedSiteIds={selectedSiteIds}
                onCancel={() => setBulkEditing(false)}
                onSuccess={() => {
                    setBulkEditing(false);
                    notify("Elements updated");
                }}
                onError={() => {
                    setBulkEditing(false);
                    notify("Elements failed to update", { type: "error" });
                }}
            />
        )) ||
        (!bulkEditing && (
            <>
                <Filter
                    filterKeys={["siteGroupNameFilter", "lineNameFilter", "lineIdsFilter"]}
                    siteGroupNameFilter={siteGroupNameFilter}
                    lineNameFilter={lineNameFilter}
                    lineIdsFilter={lineIdsFilter}
                    labels={{
                        siteGroupNameFilter: "Site Group",
                        lineNameFilter: "Line Name",
                        lineIdsFilter: "Line ID",
                    }}
                    onChange={(newFilterParams) => {
                        navigate(buildNewUrl(location, newFilterParams, false));
                    }}
                />
                <SiteBulkActionList
                    filter={[lineIdsFilter, lineNameFilter, siteGroupNameFilter]}
                    onBulkEdit={(selectedSiteIds) => {
                        setSelectedSiteIds(selectedSiteIds);
                        setBulkEditing(true);
                    }}
                    {...props}
                />
            </>
        ))
    );
};

const SiteTitle = ({ record }) => {
    return <span>Post {record ? `"${record.site_name}"` : ""}</span>;
};

export const SitesEdit = (props) => {
    const uptimeHelperText =
        "This property is read-only in this interface and provided here only for information purposes. " +
        "Its value is calculated by a combination of the site_group's retirement_date, available_to_user, and " +
        "model_type and dictates which site_groups have alerting in place for missing ratings";

    return (
        // Transform is used here to prevent editing of customer_visibility_dttm
        // via portal/sites/:id endpoint, as this field is meant to be edited only
        // via the bulk edit tool or masterdata-ui.
        <Edit title={<SiteTitle />} transform={(data) => { const { customer_visibility_dttm, ...rest } = data; return rest; }} {...props}>
            <SimpleForm validate={validateSiteCreation} toolbar={<LVEditToolbar />}>
                <TextInput inputProps={{ readOnly: true }} source="site_group_id" />
                <TextInput source="site_group_name" />
                <TextInput label="Client ID" source="client_id" />
                <TextInput source="line_owner" />
                <TextInput source="line_operator" />
                <TextInput disabled source="modelType" />
                <TextInput source="static_current_amps" />
                <TextInput source="conductor_clearance_limit_mts" />
                <TextInput source="conductor_temp_limit_c" />
                <TextInput label="External Id" source="external_id" />
                <TextInput source="latitude" />
                <TextInput source="longitude" />
                <TextInput source="voltage_nominal" />
                <TextInput source="weather_station_id" />
                <TextInput source="timezone_code" />
                <TextInput disabled source="time_stamp" />
                <TextInput disabled source="creation_date" />
                <TextInput source="status" />
                <BooleanInput label="Available To User" source="available_to_user" defaultValue="false" />
                <TextInput
                    label="Customer Visibility"
                    source="customer_visibility_dttm"
                    disabled
                    helperText="This field can only be updated via the bulk edit tool or masterdata-ui."
                />
                <TextInput
                    label="Uptime Monitored"
                    source="uptime_monitored"
                    format={(uptime_monitored) => (uptime_monitored ? "True" : "False")}
                    title={uptimeHelperText}
                    disabled={true}
                    helperText={
                        <>
                            Hover mouse over field for
                            <br />
                            explanation.
                        </>
                    }
                />
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        fontFamily: "Roboto",
                        position: "relative",
                        top: "1rem",
                        bottom: "1rem",
                    }}
                >
                    <AssignClientChart getEntityIds={getEntityIds} />
                    <span style={{ marginLeft: ".5rem" }}>Assign Client Chart</span>
                </div>
            </SimpleForm>
        </Edit>
    );
};

export const SitesCreate = (props) => (
    <Create {...props}>
        <SimpleForm toolbar={<LVCreateToolbar />}>
            <TextInput source="site_name" />
            <TextInput source="site_group_name" />
            <TextInput label="Client ID" source="client_id" />
            <TextInput source="line_owner" />
            <TextInput source="line_operator" />
            <TextInput source="modelType" />
            <TextInput source="static_current_amps" />
            <TextInput source="conductor_clearance_limit_mts" />
            <TextInput source="conductor_temp_limit_c" />
            <TextInput source="external_id" />
            <TextInput source="latitude" />
            <TextInput source="longitude" />
            <TextInput source="voltage_nominal" />
            <TextInput source="weather_station_id" />
            <TextInput source="timezone_code" />
            <TextInput source="status" />
            <BooleanInput label="Available To User" source="available_to_user" defaultValue="false" />
        </SimpleForm>
    </Create>
);

export const SitesShow = (props) => {
    return (
        <Show {...props}>
            <SimpleShowLayout>
                <TextField inputProps={{ readOnly: true }} source="site_group_id" />
                <TextField source="site_group_name" />
                <TextField label="Client ID" source="client_id" />
                <TextField source="line_owner" />
                <TextField source="line_operator" />
                <TextField source="modelType" />
                <TextField source="static_current_amps" />
                <TextField source="conductor_clearance_limit_mts" />
                <TextField source="conductor_temp_limit_c" />
                <TextField label="External Id" source="external_id" />
                <TextField source="latitude" />
                <TextField source="longitude" />
                <TextField source="voltage_nominal" />
                <TextField source="weather_station_id" />
                <TextField source="timezone_code" />
                <TextField disabled source="time_stamp" />
                <TextField disabled source="creation_date" />
                <TextField source="status" />
                <BooleanField label="Available To User" source="available_to_user" defaultValue="false" />
                <BooleanField label="Uptime Monitored" source="uptime_monitored" />
                <TextField label="Customer Visibility" source="customer_visibility_dttm" />
            </SimpleShowLayout>
        </Show>
    );
};
